import {
  type InfiniteData,
  keepPreviousData,
  type QueryKey,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import {
  type GetStardustRecordQueryResult,
  type LoyaltyPointType,
  getLoyaltiesEventRecords,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

const LOYALTIES_EVENT_RECORDS_QUERY_KEY = "LOYALTIES_EVENT_RECORDS";

export const loyaltiesEventRecordsKeys = {
  all: [LOYALTIES_EVENT_RECORDS_QUERY_KEY],
  byParams: (
    limit?: number,
    offset?: number,
    pointType?: LoyaltyPointType,
    settlementPeriodId?: number,
  ) => [
    ...loyaltiesEventRecordsKeys.all,
    limit,
    offset,
    pointType,
    settlementPeriodId,
  ],
};

type Props = {
  limit?: number;
  offset?: number;
  pointType?: LoyaltyPointType;
  settlementPeriodId?: number;
  enabled?: boolean;
};

type PageParams = {
  offset: number;
  limit: number;
};

export const useLoyaltiesEventRecordsQuery = ({
  limit,
  offset,
  pointType,
  settlementPeriodId,
  enabled = true,
}: Props = {}) =>
  useQuery<GetStardustRecordQueryResult>({
    queryKey: loyaltiesEventRecordsKeys.byParams(
      limit,
      offset,
      pointType,
      settlementPeriodId,
    ),
    queryFn: processResponseWithApiProblemDetails(() =>
      getLoyaltiesEventRecords(limit, offset, pointType, settlementPeriodId),
    ),
    enabled,
  });

export const useLoyaltiesEventRecordsInfiniteQuery = ({
  limit = 20,
  nextPageLimit = 20,
  pointType,
  settlementPeriodId,
}: {
  limit?: number;
  nextPageLimit?: number;
  pointType?: LoyaltyPointType;
  settlementPeriodId?: number;
} = {}) =>
  useInfiniteQuery<
    GetStardustRecordQueryResult,
    unknown,
    InfiniteData<GetStardustRecordQueryResult>,
    QueryKey,
    PageParams
  >({
    queryKey: loyaltiesEventRecordsKeys.byParams(
      limit,
      undefined,
      pointType,
      settlementPeriodId,
    ),
    queryFn: processResponseWithApiProblemDetails(
      ({ pageParam: { limit, offset } }) =>
        getLoyaltiesEventRecords(limit, offset, pointType, settlementPeriodId),
    ),
    placeholderData: keepPreviousData,
    initialPageParam: { offset: 0, limit },
    getNextPageParam: (
      lastPage: GetStardustRecordQueryResult | null,
      allPages,
    ) => {
      if (!lastPage) {
        return undefined;
      }

      const totalLoaded = allPages.reduce(
        (sum, page) => sum + (page.items?.length ?? 0),
        0,
      );

      const isFinalPage = totalLoaded >= (lastPage.totalCount ?? 0);

      if (isFinalPage) {
        return undefined;
      }

      return {
        offset: totalLoaded,
        limit: nextPageLimit,
      };
    },
  });
